import { useEffect, useState } from "react";

import { relativePositionToAbsolutePosition, ySyncPluginKey } from "@tiptap/y-tiptap";
import { observer } from "mobx-react-lite";
import { createRelativePositionFromJSON } from "yjs";

import { CURSOR_POSITION_CLASS_NAME } from "@/models/CursorModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorView } from "@tiptap/pm/view";
import type { Awareness } from "y-protocols/awareness";

// Viewport coordinates.
type Caret = { left: number; top: number; height: number };
export type RemoteCursor = {
  clientId: number;
  twColor: string; // tailwind color name, e.g. "green", rendered as `bg-{twColor}-500`
  name?: string;
  caret: Caret;
  posKey: string; // changes on move; used as the React key so a move restarts the fade
};

// `cursor`: y-prosemirror text selection. `expressionCursor`: in-math cursor (CursorModel).
// `user`: AwarenessModel (no name for anonymous users).
type CursorField = { anchor: unknown; head: unknown };
type ExpressionCursor = { id?: string; index?: number };
type RemoteAwarenessState = {
  cursor?: CursorField;
  expressionCursor?: ExpressionCursor;
  user?: { twColor?: string; name?: string; username?: string };
};
type YSyncState = { doc: unknown; type: unknown; binding: { mapping: unknown } };

// In-math users broadcast `expressionCursor` (focus is on the node's input, not the editor
// view). Its cursor-position marker is a zero-size anchor, so take its x and the math
// node's vertical box.
function mathCaret(view: EditorView, expr: ExpressionCursor | undefined): Caret | null {
  if (!expr || expr.id == null || expr.index == null) return null;
  const selector = `.${CURSOR_POSITION_CLASS_NAME}[data-node-id="${expr.id}"][data-cursor-index="${expr.index}"]`;
  const el = view.dom.querySelector<HTMLElement>(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  const mathRect = el.closest("[data-math]")?.getBoundingClientRect();
  if (mathRect && mathRect.height > 0) {
    return { left: rect.left, top: mathRect.top, height: mathRect.height };
  }
  return rect.height > 0 ? { left: rect.left, top: rect.top, height: rect.height } : null;
}

function textCaret(view: EditorView, ystate: YSyncState | undefined, head: unknown): Caret | null {
  if (!ystate?.binding) return null;
  const pos = relativePositionToAbsolutePosition(
    ystate.doc as never,
    ystate.type as never,
    createRelativePositionFromJSON(head) as never,
    ystate.binding.mapping as never,
  );
  if (pos === null) return null;
  try {
    const c = view.coordsAtPos(pos);
    return { left: c.left, top: c.top, height: c.bottom - c.top };
  } catch {
    return null;
  }
}

export function computeRemoteCursors(view: EditorView, awareness: Awareness): RemoteCursor[] {
  const ystate = ySyncPluginKey.getState(view.state) as YSyncState | undefined;
  const localUsername = (awareness.getLocalState() as RemoteAwarenessState | null)?.user?.username;

  const result: RemoteCursor[] = [];
  awareness.getStates().forEach((rawState, clientId) => {
    if (clientId === awareness.clientID) return;
    const state = rawState as RemoteAwarenessState;
    if (localUsername && state.user?.username === localUsername) return;

    const expr = state.expressionCursor;
    let caret = mathCaret(view, expr);
    let posKey = caret ? `m:${expr?.id}:${expr?.index}` : "";

    if (!caret && state.cursor?.head) {
      caret = textCaret(view, ystate, state.cursor.head);
      posKey = `t:${JSON.stringify(state.cursor.head)}`;
    }
    if (!caret) return;

    result.push({
      clientId,
      twColor: state.user?.twColor || "gray",
      name: state.user?.name || undefined,
      caret,
      posKey,
    });
  });
  return result;
}

function CursorMark({ cursor }: { cursor: RemoteCursor }) {
  const colorClass = `bg-${cursor.twColor}-500`;
  return (
    <div
      className="absolute animate-awareness-fade"
      style={{ top: cursor.caret.top, left: cursor.caret.left, height: cursor.caret.height }}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-full ${colorClass}`} />
      {cursor.name && (
        <div
          className={`absolute -left-2 top-full mt-0.5 whitespace-nowrap rounded-full px-1.5 py-1 text-xxs leading-none text-white ${colorClass}`}
        >
          {cursor.name}
        </div>
      )}
    </div>
  );
}

// Remote carets are a screen-space overlay rather than ProseMirror decorations: a
// decoration over a math node re-parents its node-view and blurs the in-math caret of
// whoever is editing it. The local cursor is broadcast by CollaborationCaretBroadcast.
export const AwarenessCursors = observer(function AwarenessCursors({
  doc,
}: {
  doc: DocumentModel;
}) {
  const { awareness } = doc;
  const { tiptap } = doc.editor;
  const [cursors, setCursors] = useState<RemoteCursor[]>([]);

  useEffect(() => {
    if (!awareness || !tiptap) return undefined;
    const yAwareness = awareness.provider.awareness as unknown as Awareness;

    let raf: number | null = null;
    const recompute = () => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setCursors(computeRemoteCursors(tiptap.view, yAwareness));
      });
    };

    yAwareness.on("change", recompute);
    tiptap.on("transaction", recompute);
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    recompute();

    return () => {
      yAwareness.off("change", recompute);
      tiptap.off("transaction", recompute);
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [awareness, tiptap]);

  if (!awareness || !tiptap) return null;

  return (
    <div className="fixed inset-0 z-[150] pointer-events-none">
      {cursors.map((cursor) => (
        <CursorMark key={`${cursor.clientId}-${cursor.posKey}`} cursor={cursor} />
      ))}
    </div>
  );
});
